// Interview session health (LUM-82): mid-call banner copy, what the
// browser observed during a call, and the post-failure message chosen
// from those observations.

/** What the browser saw during the call that just ended. */
export interface CallHealthObservations {
  /** The browser fired `offline`. */
  wentOffline: boolean;
  /** The tab was hidden while the call was live. */
  tabHidden: boolean;
  /** The service entered its mid-call resume window. */
  connectionDropped: boolean;
  /** The "we can't hear you" condition was raised. */
  micNoAudio: boolean;
}

export const NO_CALL_HEALTH_ISSUES: CallHealthObservations = {
  wentOffline: false,
  tabHidden: false,
  connectionDropped: false,
  micNoAudio: false,
};

export const CALL_BANNER_COPY = {
  tabHidden: 'Your interview is still running. Return to this tab to continue.',
  connectionUnstable:
    'Connection unstable. Stay on this page while we reconnect.',
  micSilent: "We can't hear you. Check your microphone.",
} as const;

// Every failure text starts "Call ended" — InterviewErrorAlert keys
// its "Start New Interview" recovery button on that prefix.
export const CALL_FAILURE_COPY = {
  network:
    'Call ended — your internet connection dropped. Check your connection, then start the interview again.',
  tabHidden:
    'Call ended while this tab was in the background. Keep this tab open and in front during your interview, then try again.',
  micNoAudio:
    "Call ended — we couldn't hear your microphone. Check that the right microphone is selected and not muted, then try again.",
} as const;

/** Root cause before symptom: an `offline` event is the network
 *  itself; a hidden tab is what usually kills the socket, so it
 *  outranks the reconnect it caused; any connection signal outranks a
 *  silent mic (a dead link also reads as silence). */
export function selectCallFailureMessage(
  observed: CallHealthObservations,
  fallback: string,
): string {
  if (observed.wentOffline) return CALL_FAILURE_COPY.network;
  if (observed.tabHidden) return CALL_FAILURE_COPY.tabHidden;
  if (observed.connectionDropped) return CALL_FAILURE_COPY.network;
  if (observed.micNoAudio) return CALL_FAILURE_COPY.micNoAudio;
  return fallback;
}

/** The end-reason text a failed call showed before LUM-82 — still the
 *  fallback when the browser observed nothing. */
export function endReasonFailureMessage(
  endReason: string | null | undefined,
): string {
  const reason = (endReason ?? '').toLowerCase();
  if (reason.includes('silence')) {
    return 'Call ended due to silence - please check your microphone is selected correctly';
  }
  if (reason.includes('ejected')) {
    return 'Call ended by the system. Please try again';
  }
  if (reason.includes('timeout')) {
    return 'Call ended - maximum or network duration reached';
  }
  if (reason.includes('hang')) {
    return 'Call ended. If this was accidental, please try again';
  }
  return 'Call ended unexpectedly - please try again';
}

/** A call that died on the wire (exhausted resumes, relay failure) —
 *  recorded as failed, never as completed (LUM-187). */
export function isFailedEndReason(
  endReason: string | null | undefined,
): boolean {
  return endReason ? /^(connection-lost|relay-failure)/.test(endReason) : false;
}
